/**
 * The chat client shared by the player and the hosted chat page
 * (docs/viewer-chat-plan.md, 3.8 and 7.3). It talks to the public chat
 * endpoints only, like any developer's own UI would:
 *
 *  - reads GET {read}/{id}/chat every poll_after_seconds (cached 2 s at the
 *    CDN: the same bytes for everyone), slower while the tab is hidden;
 *  - gets a signed viewer session only when the viewer first posts or reacts,
 *    and keeps it per browser and stream until it expires;
 *  - posts straight to the API; reactions are counted here and sent in
 *    batches, beacon-style, only while this session is sampled.
 */
export type ChatState = 'open' | 'paused' | 'read_only' | 'closed' | 'off';
export type Role = 'host' | 'moderator' | null;
export interface ChatMessage {
  id: string;
  nickname: string;
  text: string;
  role: Role;
  created_at: string;
}
export interface ChatRead {
  state: ChatState;
  slow_mode_seconds: number;
  slow_mode_auto: boolean;
  messages: ChatMessage[];
  deleted_ids: string[];
  cleared_at: string | null;
  pinned: ChatMessage | null;
  reactions_enabled: boolean;
  reactions: Record<string, number>;
  reaction_sampling: { rate: number; interval_seconds: number } | null;
  poll_after_seconds: number;
}
interface Session {
  token: string;
  u: number;
  expires_at: string;
}
export type PostResult =
  | { ok: true; message: ChatMessage }
  | { ok: false; code: string; message: string; retryAfter?: number };

/** The moderator this browser is, from a moderator link (docs/viewer-chat-plan.md, 3.6). */
export interface Moderator {
  id: string;
  name: string;
  scope: 'stream' | 'group';
}
export type ModResult = { ok: true; body: unknown } | { ok: false; code: string; message: string };

export const REACTIONS = ['heart', 'clap', 'laugh', 'fire', 'party', 'wow'] as const;
export type Reaction = (typeof REACTIONS)[number];
export const EMOJI: Record<Reaction, string> = { heart: '❤️', clap: '👏', laugh: '😂', fire: '🔥', party: '🎉', wow: '😮' };

const HIDDEN_POLL_S = 20;
const MAX_PER_EMOJI = 10;

/** localStorage that may be blocked (third-party iframes, private modes): memory then. */
const store = (() => {
  const mem = new Map<string, string>();
  return {
    get(k: string): string | null {
      try {
        return localStorage.getItem(k);
      } catch {
        return mem.get(k) ?? null;
      }
    },
    set(k: string, v: string) {
      try {
        localStorage.setItem(k, v);
      } catch {
        mem.set(k, v);
      }
    },
    del(k: string) {
      try {
        localStorage.removeItem(k);
      } catch {
        mem.delete(k);
      }
    },
  };
})();

export class ChatClient {
  last: ChatRead | null = null;
  private listeners = new Set<(r: ChatRead) => void>();
  private offlineListeners = new Set<(offline: boolean) => void>();
  private timer = 0;
  private stopped = false;
  private failures = 0;
  private session: Session | null = null;
  private sessionLoading: Promise<Session | null> | null = null;
  private pendingReactions: Partial<Record<Reaction, number>> = {};
  private reactionTimer = 0;

  constructor(
    readonly streamId: string,
    private readonly api: string,
    private readonly read: string,
  ) {
    try {
      const s = JSON.parse(store.get(this.sessionKey) ?? 'null') as Session | null;
      if (s && Date.parse(s.expires_at) > Date.now() + 60_000) this.session = s;
    } catch {
      /* none */
    }
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  private get sessionKey() {
    return `jc.session.${this.streamId}`;
  }

  get nickname(): string {
    return store.get('jc.nick') ?? '';
  }
  set nickname(n: string) {
    store.set('jc.nick', n);
  }

  onRead(fn: (r: ChatRead) => void): () => void {
    this.listeners.add(fn);
    if (this.last) fn(this.last);
    return () => this.listeners.delete(fn);
  }
  onOffline(fn: (offline: boolean) => void): () => void {
    this.offlineListeners.add(fn);
    return () => this.offlineListeners.delete(fn);
  }

  start(): void {
    this.stopped = false;
    void this.poll();
    this.reactionTimer = window.setInterval(() => void this.flushReactions(), 3000);
  }

  stop(): void {
    this.stopped = true;
    clearTimeout(this.timer);
    clearInterval(this.reactionTimer);
    void this.flushReactions();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private onVisibility = () => {
    if (!document.hidden && !this.stopped) void this.poll();
  };

  private async poll(): Promise<void> {
    clearTimeout(this.timer);
    if (this.stopped) return;
    let next = 2;
    try {
      const ctl = new AbortController();
      const t = setTimeout(() => ctl.abort(), 8000);
      const r = await fetch(`${this.read}/${this.streamId}/chat`, { credentials: 'omit', signal: ctl.signal });
      clearTimeout(t);
      if (!r.ok) throw new Error(String(r.status));
      const body = (await r.json()) as ChatRead;
      this.last = body;
      if (this.failures) this.offlineListeners.forEach((f) => f(false));
      this.failures = 0;
      this.listeners.forEach((f) => f(body));
      next = body.poll_after_seconds || 2;
    } catch {
      this.failures++;
      if (this.failures === 2) this.offlineListeners.forEach((f) => f(true));
      next = Math.min(30, 2 ** Math.min(this.failures, 5));
    }
    if (document.hidden) next = Math.max(next, HIDDEN_POLL_S);
    // A little jitter, so a crowd that opened together does not poll together.
    this.timer = window.setTimeout(() => void this.poll(), (next + Math.random() * 0.6) * 1000);
  }

  /** Polls again now (after posting, so the viewer sees others' replies sooner). */
  refreshSoon(): void {
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => void this.poll(), 1200);
  }

  private async ensureSession(force = false): Promise<Session | null> {
    if (!force && this.session && Date.parse(this.session.expires_at) > Date.now() + 60_000) return this.session;
    this.sessionLoading ??= (async () => {
      try {
        const r = await fetch(`${this.api}/v1/embed/${this.streamId}/session`, { method: 'POST', credentials: 'omit' });
        if (!r.ok) return null;
        const s = (await r.json()) as Session;
        this.session = s;
        store.set(this.sessionKey, JSON.stringify({ token: s.token, u: s.u, expires_at: s.expires_at }));
        return s;
      } catch {
        return null;
      } finally {
        this.sessionLoading = null;
      }
    })();
    return this.sessionLoading;
  }

  async post(text: string, nickname: string, retried = false): Promise<PostResult> {
    const s = await this.ensureSession(retried);
    if (!s) return { ok: false, code: 'network', message: '' };
    try {
      const r = await fetch(`${this.api}/v1/embed/${this.streamId}/chat/messages`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: s.token, nickname, text }),
      });
      const body = await r.json().catch(() => null);
      if (r.ok) {
        this.refreshSoon();
        return { ok: true, message: body as ChatMessage };
      }
      const err = (body?.error ?? {}) as { code?: string; message?: string; details?: { retry_after_seconds?: number } };
      if (err.code === 'invalid_session' && !retried) {
        store.del(this.sessionKey);
        this.session = null;
        return this.post(text, nickname, true);
      }
      return { ok: false, code: err.code ?? 'error', message: err.message ?? '', retryAfter: err.details?.retry_after_seconds };
    } catch {
      return { ok: false, code: 'network', message: '' };
    }
  }

  // ---------------------------------------------------------------- moderators
  // One moderator session per browser (a group's moderator uses it on every
  // stream of the group); the server says per stream whether it applies.

  get modToken(): string | null {
    return store.get('jc.mod');
  }

  /** Turns a moderator link (#mod=...) into this browser's moderator session. */
  async redeem(invite: string): Promise<{ ok: boolean; message: string }> {
    try {
      const r = await fetch(`${this.api}/v1/embed/${this.streamId}/moderator`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite }),
      });
      const body = await r.json().catch(() => null);
      if (!r.ok) return { ok: false, message: body?.error?.message ?? '' };
      store.set('jc.mod', body.token);
      return { ok: true, message: '' };
    } catch {
      return { ok: false, message: '' };
    }
  }

  /** Whether this browser moderates this stream. */
  async moderator(): Promise<Moderator | null> {
    const token = this.modToken;
    if (!token) return null;
    try {
      const r = await fetch(`${this.api}/v1/embed/${this.streamId}/mod`, { credentials: 'omit', headers: { 'X-Moderator-Token': token } });
      if (!r.ok) return null;
      const m = await r.json();
      return { id: m.id, name: m.name, scope: m.scope };
    } catch {
      return null;
    }
  }

  async mod(method: 'POST' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<ModResult> {
    try {
      const r = await fetch(`${this.api}/v1/embed/${this.streamId}/mod${path}`, {
        method,
        credentials: 'omit',
        headers: { 'X-Moderator-Token': this.modToken ?? '', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      const b = await r.json().catch(() => null);
      this.refreshSoon();
      return r.ok ? { ok: true, body: b } : { ok: false, code: b?.error?.code ?? 'error', message: b?.error?.message ?? '' };
    } catch {
      return { ok: false, code: 'network', message: '' };
    }
  }

  /** Counts a click; batches go out every few seconds (sampled, scaled up by the server). */
  react(r: Reaction): void {
    this.pendingReactions[r] = Math.min(MAX_PER_EMOJI, (this.pendingReactions[r] ?? 0) + 1);
    void this.ensureSession();
  }

  private async flushReactions(): Promise<void> {
    const counts = Object.entries(this.pendingReactions).filter(([, n]) => n && n > 0);
    if (!counts.length) return;
    this.pendingReactions = {};
    const sampling = this.last?.reaction_sampling;
    const s = this.session;
    // Not sampled under load: the clicks still floated on this screen.
    if (!sampling || !s || s.u >= sampling.rate) return;
    const r = counts.map(([k, n]) => `${k}:${n}`).join(',');
    const url = `${this.api}/v1/embed/${this.streamId}/chat/reactions?t=${encodeURIComponent(s.token)}&r=${r}`;
    try {
      if (!navigator.sendBeacon?.(url)) void fetch(url, { method: 'POST', keepalive: true, mode: 'no-cors', credentials: 'omit' });
    } catch {
      /* best effort */
    }
  }
}
